import axios from 'axios';
import toast from 'react-hot-toast';
import { url as API_BASE_URL } from './apiConfig';

// Create axios instance
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request Interceptor
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        const orgId = localStorage.getItem('orgId');

        if (token && !config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        if (orgId && !config.headers['X-Org-Id']) {
            config.headers['X-Org-Id'] = orgId;
        }

        // Let axios set the correct Content-Type (with boundary) for FormData
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Extracts a human-readable message from any DRF error response shape.
// Handles: { detail }, { message }, { error }, { field: ["msg"] }, { non_field_errors: ["msg"] }
const extractErrorMessage = (data) => {
    if (!data || typeof data !== 'object') return null;

    // Top-level string fields
    if (data.detail) return String(data.detail);
    if (data.message) return String(data.message);
    if (data.error) return String(data.error);

    // Field-level validation errors (DRF default format)
    const messages = [];
    for (const [field, value] of Object.entries(data)) {
        if (Array.isArray(value)) {
            const label = field === 'non_field_errors' ? '' : `${field}: `;
            messages.push(`${label}${value.join(', ')}`);
        } else if (typeof value === 'string') {
            messages.push(`${field}: ${value}`);
        }
    }

    return messages.length > 0 ? messages.join(' | ') : null;
};

// Response Interceptor
api.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        console.error('DEBUG: Axios Interceptor Global Error:', {
            message: error.message,
            code: error.code,
            response: error.response?.status,
            data: error.response?.data,
            url: originalRequest?.url
        });

        if (error.response) {
            const { data: responseData, status } = error.response;

            // Handle 401 Unauthorized
            if (status === 401 && !originalRequest._retry) {
                // Check if we have a refresh token
                const refreshToken = localStorage.getItem('refresh_token');

                if (refreshToken && !originalRequest.url.includes('/auth/refresh/')) {
                    originalRequest._retry = true;
                    try {
                        console.log('DEBUG: Attempting token refresh...');
                        // Use a dedicated axios instance or call directly to avoid interceptor loop
                        const refreshResponse = await axios.post(`${API_BASE_URL}/auth/refresh/`, {
                            refresh: refreshToken
                        });

                        const { access } = refreshResponse.data;
                        // console.log('DEBUG: Token refresh successful');

                        // Store new token
                        localStorage.setItem('token', access);

                        // Update original request header and retry
                        originalRequest.headers.Authorization = `Bearer ${access}`;
                        return api(originalRequest);
                    } catch (refreshError) {
                        console.error('DEBUG: Token refresh failed:', refreshError);
                        // Refresh failed, fall through to logout logic
                    }
                }

                // If no refresh token or refresh failed, or already retried
                toast.error('Error: Session expired or unauthorized. Please login again.');
                // Clear auth data
                localStorage.removeItem('user');
                localStorage.removeItem('token');
                localStorage.removeItem('refresh_token');
                localStorage.removeItem('orgId');

                // Redirect if not already on login page
                if (!window.location.pathname.includes('/auth-login')) {
                    setTimeout(() => {
                        window.location.href = '/auth-login';
                    }, 1000);
                }
            } else if (status !== 401) {
                // Only show other errors if NOT a 401 (which we handled above)
                // Skip toast if the caller opted out (e.g. background queries that 403 for some roles)
                if (!originalRequest._suppressToast) {
                    const message = extractErrorMessage(responseData);
                    toast.error(message ? `Error: ${message}` : `Error: ${status} - ${error.message}`);
                }
            }
        } else if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
            toast.error('Error: Cannot connect to the server. Please ensure the backend is running.');
        } else if (error.message) {
            toast.error(`Error: ${error.message}`);
        } else if (error.code) {
            toast.error(`Error Code: ${error.code}`);
        } else {
            toast.error('An unexpected error occurred. Please try again.');
        }

        return Promise.reject(error);
    }
);

export default api;
